import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import JoinNowFormPopup from "../../../componants/JoinNowFormPopup";

// Helper component for Star Rating (reused)
const StarRating = ({ rating }: { rating: number }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <svg
        key={i}
        className={`h-4 w-4 ${
          i <= rating ? "text-yellow-400" : "text-gray-300"
        } inline-block`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex items-center">{stars}</div>;
};

// Helper component for Verified Badge (reused)
const VerifiedBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ml-2">
    <svg
      className="w-3 h-3 mr-1"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
    Verified
  </span>
);

const PythonCertification = () => {
  const testimonials = [
    {
      id: 1,
      name: "Sneha Malik",
      review:
        "The Python certification course at Nadezhda Venture in Noida was outstanding! The blend of theory and practical exercises, especially in data science and machine learning, was perfect. The instructors are truly experts and very supportive. I feel well-prepared for a career in AI.",
      rating: 5,
      date: "2024-07-28",
      profileImg: "https://randomuser.me/api/portraits/women/70.jpg", // Placeholder image
    },
    {
      id: 2,
      name: "Arjun Sharma",
      review:
        "Learning Python at Nadezhda Venture has been a game-changer. From web development with Django to data analysis with Pandas, the curriculum covers everything comprehensively. The live projects were incredibly helpful in building confidence. Best Python training in Noida!",
      rating: 5,
      date: "2024-07-25",
      profileImg: "https://randomuser.me/api/portraits/men/40.jpg", // Placeholder image
    },
    {
      id: 3,
      name: "Priya Singh",
      review:
        "I joined Nadezhda Venture for Python and found it to be the best institute. The focus on real-world applications and problem-solving is remarkable. The placement support is also excellent, helping me secure an internship. Highly recommended for Python aspirants.",
      rating: 4,
      date: "2024-07-22",
      profileImg: "https://randomuser.me/api/portraits/women/80.jpg", // Placeholder image
    },
    {
      id: 4,
      name: "Rahul Kumar",
      review:
        "A truly comprehensive Python course. The instructors explain complex topics like algorithms and data structures in a very understandable way. The course also covered basics of AI and ML, which was a huge plus for me. Glad I chose Nadezhda Venture for my Python certification.",
      rating: 4,
      date: "2024-07-20",
      profileImg: "https://randomuser.me/api/portraits/men/55.jpg", // Placeholder image
    },
    {
      id: 5,
      name: "Divya Gupta",
      review:
        "Nadezhda Venture provided an excellent foundation in Python. The hands-on sessions and assignments were very practical. They guided me through building several Python projects, which really helped me grasp the concepts. Their placement team is very supportive.",
      rating: 5,
      date: "2024-07-18",
      profileImg: "https://randomuser.me/api/portraits/women/90.jpg", // Placeholder image
    },
  ];
  const [isPopupOpen, setIsPopupOpen] = useState(false); // State to control popup visibility
    const [selectedCourseTitle, setSelectedCourseTitle] = useState(""); // State to store the course title
  
    const handleOpenPopup = (courseTitle: string) => { // Function to open the popup
      setSelectedCourseTitle(courseTitle); // Set the selected course title
      setIsPopupOpen(true); // Open the popup
    };
  
    const handleClosePopup = () => { // Function to close the popup
      setIsPopupOpen(false); // Close the popup
      setSelectedCourseTitle(""); // Clear selected course when closing
    };

  const faqContent = [
    {
      title: "What is Python and its versatility?",
      answer:
        "Python is a high-level, interpreted, general-purpose programming language known for its simplicity and readability. Its versatility makes it popular in various fields like web development (Django, Flask), data science, machine learning, artificial intelligence, automation, scripting, and more. It has a vast ecosystem of libraries and a strong community.",
    },
    {
      title: "Core Python Concepts: What will I learn in basic Python?",
      answer:
        "In Core Python, you'll learn fundamentals such as syntax, data types (numbers, strings, lists, tuples, dictionaries, sets), variables, operators, conditional statements (if/elif/else), loops (for, while), functions, modules, and basic input/output operations. This forms the foundation for advanced Python applications.",
    },
    {
      title: "Object-Oriented Python (OOP): How is OOP applied in Python?",
      answer:
        "Object-Oriented Programming in Python involves concepts like classes, objects, inheritance, encapsulation, and polymorphism. Learning OOP helps in writing modular, reusable, and maintainable code, which is essential for developing complex applications and working with frameworks.",
    },
    {
      title: "File Handling and Error Handling: How do I/O and exceptions work?",
      answer:
        "Python provides robust ways to interact with files (reading from, writing to). You'll learn how to open, read, write, and close files efficiently. Exception handling (using `try`, `except`, `else`, `finally`) is crucial for building robust applications that can gracefully handle runtime errors and unexpected situations.",
    },
    {
      title: "Introduction to Python Libraries for Data Science/ML?",
      answer:
        "Our course introduces you to essential Python libraries like NumPy (for numerical operations and array manipulation), Pandas (for data analysis and manipulation), and Matplotlib/Seaborn (for data visualization). These libraries are foundational for data science, machine learning, and scientific computing.",
    },
    {
      title: "Web Development with Python: Which frameworks are covered?",
      answer:
        "While a full web development course is separate, our Python certification provides an introduction to web frameworks like **Django** and **Flask**. You'll understand the basics of building web applications, routing, templates, and database integration using Python.",
    },
    {
      title: "What kind of projects will I work on?",
      answer:
        "You'll build several hands-on projects, which may include a command-line application, a simple web application using Flask/Django, a data analysis project, or a basic machine learning model. These projects are designed to give you practical experience and build your portfolio.",
    },
    {
      title: "How does the Python Certification process work?",
      answer:
        "Upon successfully completing all course modules, practical assignments, and a final project assessment, you will be awarded a **Python Developer Certification** from Nadezhda Venture. This certificate demonstrates your proficiency and readiness for professional Python development roles.",
    },
    {
      title: "Is placement assistance provided after certification?",
      answer:
        "Absolutely. We offer dedicated placement support, which includes resume building, mock interview preparation, and connecting you with our network of hiring companies in Noida and beyond, helping you secure suitable job opportunities as a Python Developer, Data Analyst, or junior ML Engineer.",
    },
    {
      title: "What career paths can I pursue with Python Certification?",
      answer:
        "A Python certification can lead to diverse roles such as Python Developer, Web Developer (Django/Flask), Data Analyst, Junior Data Scientist, Automation Engineer, Scripting Engineer, and AI/ML Engineer. Python's broad applicability ensures many career opportunities.",
    },
  ];

  const certificationDetails = [
    {
      icon: (
        <svg
          className="h-8 w-8 text-blue-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          ></path>
        </svg>
      ),
      title: "Globally Recognized Certification",
      description:
        "Earn a certificate that validates your expertise across various Python applications, boosting your career globally.",
    },
    {
      icon: (
        <svg
          className="h-8 w-8 text-blue-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M10 21h7a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
          ></path>
        </svg>
      ),
      title: "Practical Project Experience",
      description:
        "Develop a robust portfolio by working on multiple real-world Python projects, covering diverse applications.",
    },
    {
      icon: (
        <svg
          className="h-8 w-8 text-blue-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          ></path>
        </svg>
      ),
      title: "Versatile Skill Set (Web, Data, AI)",
      description:
        "Gain skills applicable to web development, data science, machine learning, and automation with Python.",
    },
    {
      icon: (
        <svg
          className="h-8 w-8 text-blue-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 8c1.657 0 3 .895 3 2s-1.343 2-3 2-3-.895-3-2 1.343-2 3-2zM21 12c0 8-9 16-9 16S3 20 3 12a9 9 0 1118 0z"
          ></path>
        </svg>
      ),
      title: "Strong Placement Support",
      description:
        "Benefit from our robust placement assistance and connect with leading companies hiring Python professionals.",
    },
    {
      icon: (
        <svg
          className="h-8 w-8 text-blue-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z"
          ></path>
        </svg>
      ),
      title: "Expert-Led Training",
      description:
        "Learn from industry professionals with extensive experience in Python development and its specialized fields.",
    },
    {
      icon: (
        <svg
          className="h-8 w-8 text-blue-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V7m-4 5h-4m4 0H9m7 0H9m7 0a2 2 0 002-2V9a2 2 0 00-2-2h-4a2 2 0 00-2 2v3"
          ></path>
        </svg>
      ),
      title: "Data Science & AI/ML Foundation",
      description:
        "Get introduced to crucial libraries and concepts for building a strong foundation in Data Science and AI/ML.",
    },
  ];

  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);

  useEffect(() => {
    const autoScrollInterval = setInterval(() => {
      setCurrentTestimonialIndex(
        (prevIndex) => (prevIndex + 1) % testimonials.length
      );
    }, 5000); // Auto-slide every 5 seconds

    return () => clearInterval(autoScrollInterval); // Clean up the interval
  }, [testimonials.length]);

  const goToPrevTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const goToNextTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      (prevIndex + 1) % testimonials.length
    );
  };

  // Determine how many testimonials to show at once (e.g., 1 on small, 2 on medium, 3 on large)
  const getVisibleTestimonialsCount = () => {
    if (window.innerWidth >= 1024) return 3; // lg breakpoint
    if (window.innerWidth >= 768) return 2; // md breakpoint
    return 1; // default for small screens
  };

  const [visibleCount, setVisibleCount] = useState(
    getVisibleTestimonialsCount()
  );

  useEffect(() => {
    const handleResize = () => {
      setVisibleCount(getVisibleTestimonialsCount());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const startIndex = currentTestimonialIndex;
  const endIndex =
    startIndex + visibleCount > testimonials.length
      ? testimonials.length
      : startIndex + visibleCount;
  const currentVisibleTestimonials = [];

  for (let i = startIndex; i < endIndex; i++) {
    currentVisibleTestimonials.push(testimonials[i]);
  }

  // Handle wrapping around for fewer than visibleCount items at the end
  if (
    currentVisibleTestimonials.length < visibleCount &&
    testimonials.length >= visibleCount
  ) {
    const remaining = visibleCount - currentVisibleTestimonials.length;
    for (let i = 0; i < remaining; i++) {
      currentVisibleTestimonials.push(testimonials[i]);
    }
  } else if (testimonials.length < visibleCount) {
    // If total testimonials are less than visible count, just show all
    currentVisibleTestimonials.push(...testimonials);
  }

  return (
    <div className="bg-white text-gray-800">
      <Helmet>
        <title>Python Certification Training in Noida | Data Science & Web Dev</title>
        <meta
          name="description"
          content="Get certified in Python development in Noida. Our course covers Core Python, Data Science, AI/ML basics, Web Development (Django/Flask), and provides 100% placement assistance. Start your career in Python today!"
        />
        <meta
          name="keywords"
          content="Python training Noida, Python certification, Data Science with Python, Machine Learning Python, AI Python, Web Development Python, Django, Flask, Python placement Noida"
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden text-white h-[400px] bg-gray-900 flex items-center">
        <img
          src="https://static.vecteezy.com/system/resources/thumbnails/005/170/899/small_2x/python-programming-language-programing-workflow-abstract-algorithm-concept-on-virtual-screen-photo.jpg"
          alt="Python Development Background"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />

        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-60 z-10" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 md:py-40 flex flex-col md:flex-row items-center md:items-start">
          <div className="md:w-1/2">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-extrabold leading-tight text-left"
            >
              <span className="text-orange-500">Python</span> Certification Training in Noida
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="mt-6 text-lg md:text-xl text-gray-200 text-left"
            >
              Master Python for Web Development, Data Science, AI/ML, and Automation. Get hands-on training and achieve industry-recognized certification.
            </motion.p>

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="mt-8 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-full shadow-lg transition duration-300 ease-in-out"
            >
              <button
                    // className="bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-lg font-semibold transition text-white"
                    onClick={() => handleOpenPopup("Python Certification Course")} // Pass the course title
                  >
                    Enroll Now !
                  </button>
            </motion.button>
          </div>
        </div>
      </section>

      {/* Certification Highlights Section */}
      <section className="bg-gradient-to-r from-blue-50 to-green-50 py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-blue-900">
            Why Choose Our Python Certification?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {certificationDetails.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center"
              >
                <div className="mb-4">{item.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-700">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Content / FAQ Section */}
      <section className="max-w-6xl mx-auto px-4 py-16 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center md:text-left">
            Frequently Asked Questions (FAQ) & Course Curriculum Highlights
          </h2>
          <div className="space-y-4">
            {faqContent.map((section, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {section.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{section.answer}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>

        {/* Enquiry Form */}
        <div className="md:col-span-1 pt-48">
          <div className="bg-blue-300 rounded-lg p-6 shadow-xl">
            <h3 className="text-2xl font-bold mb-4 text-center text-blue-800">
              Enquire Now
            </h3>
            <p className="text-center text-gray-600 mb-6">
              Fill out the form below to get more details about our Python certification program.
            </p>
            <form className="space-y-4">
              <input
                type="text"
                placeholder="Your Name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="Course of Interest (e.g., Python Certification)"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="College/University Name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="email"
                placeholder="Your Email"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="tel"
                placeholder="Phone Number"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <button
                type="submit"
                className="w-full bg-blue-800 hover:bg-blue-700 text-white py-3 rounded-md font-semibold transition duration-300 ease-in-out shadow-md hover:shadow-lg"
              >
                GET CERTIFIED
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Testimonial Section - No Horizontal Scrollbar */}
      <section className="bg-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 text-center relative">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-blue-900">
            Our Students Say
          </h2>

          <div className="relative overflow-hidden w-full mx-auto">
            {/* Left Scroll Button */}
            <button
              onClick={goToPrevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Previous testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>

            {/* Testimonial Cards Container */}
            <div className="flex justify-center items-stretch space-x-6 md:space-x-8 lg:space-x-10">
              <AnimatePresence mode="wait" initial={false}>
                {currentVisibleTestimonials.map((testimonial, idx) => (
                  <motion.div
                    key={testimonial.id} // Key by testimonial ID for consistent animation
                    initial={{ opacity: 0, x: idx * 100 }} // Initial position based on index in visible set
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="flex-none w-full md:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-2rem)] p-2"
                    // w-[calc(50%-1.5rem)] means 50% width minus half of the space-x-6 (3rem total gap)
                    // w-[calc(33.333%-2rem)] means 33.333% width minus two-thirds of the space-x-6 (4rem total gap)
                  >
                    <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 flex flex-col h-full text-left">
                      <div className="flex items-center mb-4">
                        <img
                          src={testimonial.profileImg}
                          alt={testimonial.name}
                          className="w-12 h-12 rounded-full object-cover mr-4 border-2 border-blue-200"
                        />
                        <div>
                          <p className="font-bold text-gray-900 text-lg">
                            {testimonial.name}
                          </p>
                          <p className="text-gray-500 text-sm">
                            {testimonial.date}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center mb-3">
                        <StarRating rating={testimonial.rating} />
                        {testimonial && <VerifiedBadge />}
                      </div>
                      <p className="text-gray-700 text-base leading-relaxed flex-grow line-clamp-4">
                        {testimonial.review}
                      </p>
                      <a
                        href="#"
                        className="text-blue-600 hover:underline mt-4 text-sm text-right"
                      >
                        Read more
                      </a>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Right Scroll Button */}
            <button
              onClick={goToNextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Next testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                ></path>
              </svg>
            </button>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                className={`h-3 w-3 rounded-full ${
                  // Logic to highlight dots based on the current set of visible testimonials
                  idx >= currentTestimonialIndex &&
                  idx < currentTestimonialIndex + visibleCount
                    ? "bg-blue-600"
                    : "bg-gray-300"
                } transition-colors duration-300`}
                onClick={() => setCurrentTestimonialIndex(idx)}
                aria-label={`Go to testimonial group ${idx + 1}`}
              ></button>
            ))}
          </div>
        </div>
      </section>

       {/* Conditionally render the popup */}
      {isPopupOpen && (
        <JoinNowFormPopup
          onClose={handleClosePopup}
          courseTitle={selectedCourseTitle}
        />
      )}
    </div>
  );
};

export default PythonCertification;